import React, {useState} from 'react';
import {IProps} from './types';
import style from './style.module.scss';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField, withStyles,
} from '@material-ui/core';
import TablePagination from '@material-ui/core/TablePagination';
import {makeStyles} from "@material-ui/core/styles";
import {IPurchasedItemInfo} from "app/app.types";
import moment from "moment";
import {IntlService} from "external-services/intl-service/index";

const NS = 'BeverageHistoryPage';

const useStyles = makeStyles({
  root: {
    width: '100%',
  },
  container: {
    maxHeight: 440,
  },
});

const StyledTableCell = withStyles((theme) => ({
  head: {
    backgroundColor: '#e3e3e3',
    color: theme.palette.common.black,
    fontWeight: 'bold',
    borderBottom: '1px solid darkgray'
  },
  body: {
    fontSize: 14,
  },
}))(TableCell);

const StyledTableRow = withStyles((theme) => ({
  root: {
    '&:nth-of-type(odd)': {
      backgroundColor: theme.palette.action.hover,
    },
    '&:nth-of-type(even)': {
      backgroundColor: theme.palette.action.disabledBackground,
    },
  }
}))(TableRow);

interface Column {
  id: 'productName' | 'unitPrice' | 'transactionDateTime' | 'quantity' | 'description'; // data-items
  label: string;
  minWidth?: number;
  align?: 'right';
  format?: (value: any) => any;
}

/** Displays the list of beverages */
export default function BeverageHistoryPage(props: IProps) {

  const columns: Column[] = [
    {
      id: 'transactionDateTime',
      label: 'Date of purchase',
      minWidth: 100,
      format: (value: string) => moment(value).format('DD MMM YY h:mma')
    },
    {id: 'productName', label: 'Item', minWidth: 150},
    {
      id: 'quantity',
      label: 'Quantity',
      minWidth: 50,
    },
    {
      id: 'description',
      label: 'Description',
      minWidth: 200,
    },
    {
      id: 'unitPrice', label: 'Unit Price', minWidth: 50,
      format: (value: number) => getPriceValue(value, props.currency)
    },

  ];

  const getPriceValue = (price: number, currency: string)=> {
    const priceVal = price > 0 ? parseFloat((price/100).toFixed(2)) : 0;
    return IntlService.currencyValueAsString(priceVal, currency)
  }

  const classes = useStyles();

  // Do not render, incase of empty list.
  if (!props.beverageList || props.beverageList.length === 0) return null;

  const [beverageList, setBeverageList] = useState<IPurchasedItemInfo[]>(props.beverageList.sort((a, b) => b.unitPrice - a.unitPrice));
  const [filteredList, setFilteredList] = useState<IPurchasedItemInfo[]>(beverageList);

  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(5);


  /**
   * Filter the beverage list as per the entered input.
   */
  const filterBeverages = (inputText: string) => {
    const beverages = props.beverageList.filter(beverage => beverage.productName.toLowerCase().includes(inputText.toLowerCase()));
    setPage(0);
    setFilteredList(beverages);
  }

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  return (
    <>
      <div className={style.searchTextField}>
        <TextField label="Search" InputLabelProps={{ shrink: true }} placeholder={"Search"} onChange={e => filterBeverages(e.target.value)}/>
      </div>
      <div>
        <Paper elevation={0} className={classes.root}>
          <TableContainer className={classes.container}>
            <Table stickyHeader aria-label="sticky table">
              <TableHead>
                <StyledTableRow>
                  {columns.map((column) => (
                    <StyledTableCell
                      key={column.id}
                      align={column.align}
                      style={{minWidth: column.minWidth}}
                    >
                      {column.label}
                    </StyledTableCell>
                  ))}
                </StyledTableRow>
              </TableHead>
              <TableBody>
                {filteredList.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row, index) => {
                  return (
                    <StyledTableRow role="checkbox" tabIndex={-1} key={index}>
                      {columns.map((column) => {
                        const value = row[column.id];
                        return (
                          <TableCell key={column.id} align={column.align}>
                            {column.format ? column.format(value) : value}
                          </TableCell>
                        );
                      })}
                    </StyledTableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10]}
            count={filteredList.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onChangePage={handleChangePage}
            onChangeRowsPerPage={handleChangeRowsPerPage}
          />
        </Paper>
      </div>
    </>
  )
}
